/**
 * PageHeader Component
 *
 * Shared page header with title, subtitle, and optional AI assistant button.
 * Used on every page to provide consistent branding and contextual help.
 */
import React, { useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Title1,
  Body1,
  Button,
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
} from '@fluentui/react-components';
import { Sparkle24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { AINarratorChat } from '../../features/ai-narrator';

export interface PageHeaderProps {
  title: string;
  subtitle: string;
  icon?: React.ReactNode;
  iconColor?: string;
  showAIButton?: boolean;
  actions?: React.ReactNode;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    flex: 1,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  subtitle: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  aiButton: {
    color: '#8852E0',
  },
});

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon,
  iconColor,
  showAIButton = true,
  actions,
}) => {
  const styles = useStyles();
  const [isAIOpen, setIsAIOpen] = useState(false);

  const handleAIOpen = useCallback(() => {
    setIsAIOpen(true);
  }, []);

  const handleAIClose = useCallback(() => {
    setIsAIOpen(false);
  }, []);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.left}>
          <div className={styles.titleRow}>
            {icon && (
              <span style={{ fontSize: '1.5rem', color: iconColor, display: 'flex' }}>
                {icon}
              </span>
            )}
            <Title1>{title}</Title1>
          </div>
          <Body1 className={styles.subtitle}>{subtitle}</Body1>
        </div>
        <div className={styles.right}>
          {actions}
          {showAIButton && (
            <Button
              appearance="subtle"
              icon={<Sparkle24Regular />}
              className={styles.aiButton}
              onClick={handleAIOpen}
            >
              Ask LeftShield AI
            </Button>
          )}
        </div>
      </div>

      {/* AI Chat Drawer */}
      <Drawer
        open={isAIOpen}
        onOpenChange={(_, data) => setIsAIOpen(data.open)}
        position="end"
        size="medium"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={handleAIClose}
                aria-label="Close AI assistant"
              />
            }
          >
            LeftShield AI Assistant
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <AINarratorChat currentPage={title} />
        </DrawerBody>
      </Drawer>
    </>
  );
};
